import React, { useState, useEffect, useCallback } from "react";
import { HelpCircle, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { HELP, pickForRole } from "../help/helpContent";

const F = "'Montserrat',sans-serif";
const SEEN_KEY = "compass_help_seen";

// localStorage set of "role|page" pairs already auto-shown to this user.
function loadSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
}
function markSeen(seen, key) {
  seen.add(key);
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...seen])); } catch { /* ignore */ }
}

export default function HelpButton() {
  const { view, currentRole } = useApp();
  const [open, setOpen] = useState(false);

  const entry = HELP[view] || null;
  const key = `${currentRole}|${view}`;

  // First-visit auto-open: open once per (role, page), then remember.
  useEffect(() => {
    if (!entry) return;
    const seen = loadSeen();
    if (!seen.has(key)) {
      setOpen(true);
      markSeen(seen, key);
    } else {
      setOpen(false);
    }
  }, [key, entry]);

  // Esc to close
  const onKey = useCallback((e) => { if (e.key === "Escape") setOpen(false); }, []);
  useEffect(() => {
    if (!open) return;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onKey]);

  // No help on the marketing landing page.
  if (view === "landing") return null;

  const title = pickForRole(entry?.title, currentRole) || "This page";
  const what  = pickForRole(entry?.what, currentRole);
  const why   = pickForRole(entry?.why, currentRole);
  const steps = pickForRole(entry?.steps, currentRole) || [];

  return (
    <>
      {/* Floating launcher — same spot on every page */}
      <button
        onClick={() => setOpen(o => !o)}
        title="What is this page?"
        aria-label="Help — what is this page?"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 1000,
          width: 44, height: 44, borderRadius: "50%",
          background: "#E8431A", color: "#fff", border: "none",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 14px rgba(29,78,216,0.4)", transition: "transform 0.12s",
        }}
        onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.08)")}
        onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
      >
        <HelpCircle size={22} />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 1001 }} />

          {/* Slide-in panel */}
          <div style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: 380, maxWidth: "90vw",
            background: "#fff", zIndex: 1002, boxShadow: "-8px 0 28px rgba(0,0,0,0.18)",
            display: "flex", flexDirection: "column", fontFamily: F,
          }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "18px 20px",
              borderBottom: "1px solid #eef0f3" }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "#FCEBE5",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <HelpCircle size={16} color="#E8431A" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 8.5, fontWeight: 800, color: "#AFABAB",
                  textTransform: "uppercase", letterSpacing: 0.6 }}>Page Guide · {currentRole}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#26262A" }}>{title}</div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#AFABAB",
                  padding: 4, lineHeight: 0 }}>
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px", display: "flex",
              flexDirection: "column", gap: 20 }}>
              {!entry ? (
                <div style={{ fontSize: 12, color: "#818084", lineHeight: 1.7 }}>
                  No guide has been written for this page yet.
                </div>
              ) : <>
                <Section label="What is this page">
                  <p style={pStyle}>{what}</p>
                </Section>

                {why && (
                  <Section label="Why you're seeing it">
                    <p style={pStyle}>{why}</p>
                  </Section>
                )}

                {steps.length > 0 && (
                  <Section label="What to do here">
                    <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none",
                      display: "flex", flexDirection: "column", gap: 8 }}>
                      {steps.map((s, i) => (
                        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <span style={{ flexShrink: 0, width: 18, height: 18, borderRadius: "50%",
                            background: "#FCEBE5", color: "#E8431A", fontSize: 10, fontWeight: 800,
                            display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                            {i + 1}
                          </span>
                          <span style={{ ...pStyle, margin: 0 }}>{s}</span>
                        </li>
                      ))}
                    </ol>
                  </Section>
                )}

              </>}
            </div>

            {/* Footer */}
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eef0f3" }}>
              <button onClick={() => setOpen(false)}
                style={{ width: "100%", padding: "9px 0", borderRadius: 8, border: "none",
                  background: "#E8431A", color: "#fff", fontSize: 12, fontWeight: 700,
                  cursor: "pointer", fontFamily: F }}>
                Got it
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

const pStyle = { margin: 0, fontSize: 12, color: "#3F3F46", lineHeight: 1.65 };

function Section({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 800, color: "#AFABAB", textTransform: "uppercase",
        letterSpacing: 0.6, marginBottom: 7 }}>{label}</div>
      {children}
    </div>
  );
}
